import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
    Bot,
    ChevronDown,
    ChevronUp,
    ExternalLink,
    Eye,
    EyeOff,
    MessageSquare,
    Power,
    Sparkles,
} from "lucide-react";
import { toast } from "sonner";
import {
    AKIL_WIDGET_CLOSE_EVENT,
    AKIL_WIDGET_OPEN_EVENT,
    AKIL_WIDGET_PREF_EVENT,
    closeAkilWidget,
    isAkilWidgetAutoOpen,
    isAkilWidgetEnabled,
    openAkilWidget,
    setAkilWidgetAutoOpen,
    setAkilWidgetEnabled,
} from "../../lib/akilWidgetPrefs";

export default function AkilWidgetControlPanel({ compact = false }) {
    const [enabled, setEnabled] = useState(isAkilWidgetEnabled);
    const [autoOpen, setAutoOpen] = useState(isAkilWidgetAutoOpen);
    const [widgetOpen, setWidgetOpen] = useState(false);
    const [expanded, setExpanded] = useState(!compact);

    const sync = useCallback(() => {
        setEnabled(isAkilWidgetEnabled());
        setAutoOpen(isAkilWidgetAutoOpen());
    }, []);

    useEffect(() => {
        sync();
        const onOpen = () => setWidgetOpen(true);
        const onClose = () => setWidgetOpen(false);
        window.addEventListener(AKIL_WIDGET_PREF_EVENT, sync);
        window.addEventListener(AKIL_WIDGET_OPEN_EVENT, onOpen);
        window.addEventListener(AKIL_WIDGET_CLOSE_EVENT, onClose);
        return () => {
            window.removeEventListener(AKIL_WIDGET_PREF_EVENT, sync);
            window.removeEventListener(AKIL_WIDGET_OPEN_EVENT, onOpen);
            window.removeEventListener(AKIL_WIDGET_CLOSE_EVENT, onClose);
        };
    }, [sync]);

    const toggleEnabled = () => {
        const next = !enabled;
        setAkilWidgetEnabled(next);
        setEnabled(next);
        if (!next) closeAkilWidget();
        toast.success(next ? "Akıl widget etkinleştirildi" : "Akıl widget gizlendi");
    };

    const toggleAutoOpen = () => {
        const next = !autoOpen;
        setAkilWidgetAutoOpen(next);
        setAutoOpen(next);
        toast.success(next ? "Merkez açılınca widget otomatik açılacak" : "Otomatik açma kapalı");
    };

    return (
        <div className="rounded-xl border border-[#D4AF37]/30 bg-gradient-to-br from-[#121A2F]/90 via-[#0F1729]/80 to-[#0B0F19]/90 overflow-hidden">
            <div className="h-0.5 bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent" />
            <div className="p-4">
                <div className="flex flex-wrap items-center gap-3">
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                        <span className="h-10 w-10 rounded-xl bg-[#D4AF37]/15 border border-[#D4AF37]/35 flex items-center justify-center shrink-0">
                            <Sparkles className="h-5 w-5 text-[#F3E5AB]" />
                        </span>
                        <div className="min-w-0">
                            <p className="text-[10px] uppercase tracking-[0.18em] text-[#D4AF37]">
                                Yüzen Asistan
                            </p>
                            <h3 className="text-sm font-semibold text-white">Akıl Widget Kontrolü</h3>
                            <p className="text-[11px] text-slate-500 mt-0.5">
                                Sağ alt köşedeki sohbet balonunu buradan yönetin
                            </p>
                        </div>
                    </div>

                    <div className="flex flex-wrap items-center gap-2">
                        <span
                            className={`inline-flex items-center gap-1.5 text-[10px] px-2.5 py-1 rounded-full border ${
                                enabled
                                    ? widgetOpen
                                        ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-300"
                                        : "border-sky-500/40 bg-sky-500/10 text-sky-300"
                                    : "border-slate-500/40 bg-slate-500/10 text-slate-400"
                            }`}
                        >
                            <span
                                className={`h-1.5 w-1.5 rounded-full ${
                                    enabled ? (widgetOpen ? "bg-emerald-400 animate-pulse" : "bg-sky-400") : "bg-slate-500"
                                }`}
                            />
                            {!enabled ? "Kapalı" : widgetOpen ? "Açık" : "Hazır"}
                        </span>

                        <button
                            type="button"
                            onClick={() => setExpanded((v) => !v)}
                            className="h-8 w-8 rounded-lg border border-white/10 text-slate-400 hover:text-white flex items-center justify-center"
                            aria-label={expanded ? "Daralt" : "Genişlet"}
                        >
                            {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                        </button>
                    </div>
                </div>

                {expanded ? (
                    <div className="mt-4 space-y-4">
                        <div className="grid sm:grid-cols-2 gap-3">
                            <label className="flex items-center justify-between gap-3 rounded-lg border border-white/10 bg-black/20 px-3 py-2.5 cursor-pointer hover:border-[#D4AF37]/30">
                                <div className="flex items-center gap-2 min-w-0">
                                    {enabled ? (
                                        <Eye className="h-4 w-4 text-emerald-400 shrink-0" />
                                    ) : (
                                        <EyeOff className="h-4 w-4 text-slate-500 shrink-0" />
                                    )}
                                    <div>
                                        <div className="text-xs font-medium text-white">Widget göster</div>
                                        <div className="text-[10px] text-slate-500">Tüm panelde sağ alt balon</div>
                                    </div>
                                </div>
                                <input
                                    type="checkbox"
                                    checked={enabled}
                                    onChange={toggleEnabled}
                                    className="rounded border-white/20 shrink-0"
                                />
                            </label>

                            <label className="flex items-center justify-between gap-3 rounded-lg border border-white/10 bg-black/20 px-3 py-2.5 cursor-pointer hover:border-[#D4AF37]/30">
                                <div className="flex items-center gap-2 min-w-0">
                                    <Power className="h-4 w-4 text-violet-400 shrink-0" />
                                    <div>
                                        <div className="text-xs font-medium text-white">Merkezde otomatik aç</div>
                                        <div className="text-[10px] text-slate-500">Bu sayfaya girince widget açılır</div>
                                    </div>
                                </div>
                                <input
                                    type="checkbox"
                                    checked={autoOpen}
                                    onChange={toggleAutoOpen}
                                    disabled={!enabled}
                                    className="rounded border-white/20 shrink-0 disabled:opacity-40"
                                />
                            </label>
                        </div>

                        <div className="flex flex-wrap gap-2">
                            <button
                                type="button"
                                onClick={() => {
                                    if (!enabled) {
                                        setAkilWidgetEnabled(true);
                                        setEnabled(true);
                                    }
                                    openAkilWidget();
                                    toast.success("Akıl widget açıldı");
                                }}
                                className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-[#D4AF37] text-slate-900 text-xs font-semibold hover:bg-[#F3E5AB]"
                            >
                                <Bot className="h-3.5 w-3.5" />
                                Widget&apos;ı Aç
                            </button>
                            <button
                                type="button"
                                onClick={() => {
                                    closeAkilWidget();
                                    toast.info("Akıl widget kapatıldı");
                                }}
                                disabled={!enabled}
                                className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-white/15 text-xs text-slate-300 hover:text-white disabled:opacity-40"
                            >
                                <ChevronDown className="h-3.5 w-3.5" />
                                Widget&apos;ı Kapat
                            </button>
                            <Link
                                to="/ai/sohbet"
                                className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-white/15 text-xs text-slate-300 hover:text-[#D4AF37]"
                            >
                                <ExternalLink className="h-3.5 w-3.5" />
                                Tam ekran sohbet
                            </Link>
                            <Link
                                to="/ai/gunluk-brifing"
                                className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-white/15 text-xs text-slate-300 hover:text-[#D4AF37]"
                            >
                                <MessageSquare className="h-3.5 w-3.5" />
                                Günlük brifing
                            </Link>
                        </div>
                    </div>
                ) : null}
            </div>
        </div>
    );
}
